import { useEffect, useState } from "react";
import { Container } from "react-bootstrap";
import { Link } from "react-router-dom";
import "./knowledge.css";

const VIDEO_IDS = ["oAdYV4FDz10", "lNKhHN6lWus", "W-urmpkH7Ik", "l8dt6SyE5FY"];
const SUBSTACK_URL = "https://glencallahan.substack.com";
const FEED_ENDPOINT = "/.netlify/functions/substack-feed";
const EXCERPT_LENGTH = 200;

function stripHtml(html) {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
}

function formatDate(pubDate) {
  const date = new Date(pubDate);
  if (isNaN(date)) return "";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function parseFeed(xml) {
  const doc = new DOMParser().parseFromString(xml, "text/xml");
  const items = Array.from(doc.querySelectorAll("item")).slice(0, 6);

  return items.map((item) => {
    const title = item.querySelector("title")?.textContent ?? "";
    const link = item.querySelector("link")?.textContent ?? "";
    const pubDate = item.querySelector("pubDate")?.textContent ?? "";
    const rawDescription = item.querySelector("description")?.textContent ?? "";
    const description = stripHtml(rawDescription).trim().slice(0, EXCERPT_LENGTH);

    return { title, link, pubDate, description };
  });
}

function Knowledge() {
  const [articles, setArticles] = useState([]);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    let cancelled = false;

    fetch(FEED_ENDPOINT)
      .then((res) => {
        if (!res.ok) throw new Error("Feed request failed");
        return res.text();
      })
      .then((xml) => {
        if (cancelled) return;
        setArticles(parseFeed(xml));
        setStatus("success");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <section className="knowledge__header">
        <Container>
          <h1 className="knowledge__title">Knowledge</h1>
          <p className="knowledge__subtitle">
            Insights on self-development, coaching, and living with intention.
          </p>
          <div className="knowledge__members-cta">
            <Link to="/members" className="knowledge__members-link">
              Members Login
            </Link>
          </div>
        </Container>
      </section>

      <section className="knowledge__section">
        <Container>
          <h2 className="knowledge__heading">Featured Videos</h2>
          <div className="knowledge__video-grid">
            {VIDEO_IDS.map((id) => (
              <div className="knowledge__video-wrapper" key={id}>
                <iframe
                  src={`https://www.youtube.com/embed/${id}`}
                  title={`Featured video ${id}`}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className="knowledge__section knowledge__section--alt">
        <Container>
          <h2 className="knowledge__heading">Latest Articles</h2>

          {status === "loading" && (
            <p className="knowledge__status">Loading articles…</p>
          )}

          {status === "error" && (
            <p className="knowledge__status">
              Articles are temporarily unavailable. Visit{" "}
              <a href={SUBSTACK_URL} target="_blank" rel="noreferrer">
                Glen's Substack
              </a>{" "}
              directly.
            </p>
          )}

          {status === "success" && articles.length === 0 && (
            <p className="knowledge__status">No articles found.</p>
          )}

          {status === "success" && articles.length > 0 && (
            <div className="knowledge__article-grid">
              {articles.map((article) => (
                <a
                  className="knowledge__article-card"
                  key={article.link}
                  href={article.link}
                  target="_blank"
                  rel="noreferrer"
                >
                  <h3 className="knowledge__article-title">{article.title}</h3>
                  <p className="knowledge__article-date">
                    {formatDate(article.pubDate)}
                  </p>
                  <p className="knowledge__article-excerpt">
                    {article.description}…
                  </p>
                </a>
              ))}
            </div>
          )}

          <div className="knowledge__more">
            <a href={SUBSTACK_URL} target="_blank" rel="noreferrer">
              Read more on Substack
            </a>
          </div>
        </Container>
      </section>
    </>
  );
}

export default Knowledge;
